import React, { memo, useEffect, useMemo } from "react";
import qs from "qs";
import { LANGUAGE } from "_constants";
import { MatchSelector } from "containers/MatchSelector";
import { DebugMode } from "containers/DebugMode";
import { Simulator } from "containers/Simulator";
import { ThemeSetter } from "containers/ThemeSetter";
import { Header } from "components/landing/Header";
import { Hero } from "components/landing/Hero";
import { SimulatorShell } from "components/SimulatorShell";
import { Highlights } from "components/landing/Highlights";
import { DataSection } from "components/landing/DataSection";
import { SportsSlider } from "components/landing/SportsSlider";
import { ContactSection } from "components/landing/ContactSection";
import { Footer } from "components/landing/Footer";
import { useMatchDictionary } from "hooks/useMatchDictionary";
import {
  isThemePreviewMode,
  THEME_PREVIEW_STORAGE_KEY,
  THEME_PREVIEW_FRAME_NAME,
} from "utils/theme-preview-storage";

export const App = memo(() => {
  const { eventId, lang, isDebugPage, isThemeSetterPage } = useMemo(() => {
    const pathSegments = window.location.pathname.split("/").filter(Boolean);
    const firstSegment = pathSegments[0] ?? "";
    let { lang: langString } = qs.parse(window.location.search.slice(1));
    const lang = (Number(langString) || 0) as LANGUAGE;
    const isDebugPage = firstSegment === "debug";
    const isThemeSetterPage = firstSegment === "theme-setter";
    const eventId =
      !isDebugPage && !isThemeSetterPage && firstSegment
        ? firstSegment
        : undefined;

    return { eventId, lang, isDebugPage, isThemeSetterPage };
  }, []);

  const { dictionary } = useMatchDictionary();
  const isThemePreview = isThemePreviewMode();
  useEffect(() => {
    if (!isThemePreview) {
      return;
    }
    const handleStorageUpdate = (event: StorageEvent) => {
      if (event.key === THEME_PREVIEW_STORAGE_KEY) {
        window.location.reload();
      }
    };
    window.addEventListener("storage", handleStorageUpdate);
    return () => window.removeEventListener("storage", handleStorageUpdate);
  }, [isThemePreview]);

  useEffect(() => {
    if (!isThemePreview) {
      return;
    }
    try {
      if (window.name !== THEME_PREVIEW_FRAME_NAME) {
        window.name = THEME_PREVIEW_FRAME_NAME;
      }
    } catch {
      // Ignore assignment failures (cross-origin / browser restrictions)
    }
  }, [isThemePreview]);
  const isEmbedded = useMemo(() => {
    if (isThemePreview) {
      return false;
    }
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }, [isThemePreview]);

  if (isThemeSetterPage) {
    return <ThemeSetter />;
  }

  if (!isDebugPage && !eventId)
    return (
      <div className="overflow-x-hidden">
        <Header />
        <Hero />
        <MatchSelector />
        <Highlights />
        <DataSection />
        <SportsSlider />
        <ContactSection />
        <Footer />
      </div>
    );

  if (isDebugPage) return <DebugMode />;

  const getMatchBreadcrumb = () => {
    if (!eventId) return "Match Center";

    // Search through all sports in dictionary to find the match
    for (const matches of Object.values(dictionary)) {
      if (!matches) continue;
      const match = matches.find((m) => m.id === eventId);
      if (match) {
        const team1 = match.T1n || match.T1?.n || "Team 1";
        const team2 = match.T2n || match.T2?.n || "Team 2";
        return `${team1} - ${team2} (${eventId})`;
      }
    }

    // Fallback if match not found in dictionary
    return eventId.replace(/-/g, " ");
  };

  const simulatorView = <Simulator eventId={eventId!} lang={lang} />;

  if (isEmbedded) return simulatorView;

  return (
    <SimulatorShell breadcrumb={["Homepage", getMatchBreadcrumb()]}>
      {simulatorView}
    </SimulatorShell>
  );
});
